// This is the only file which knows that we use axios. Keep the implementation details hidden.

import axios, {
  type Method as AxiosMethod,
  type AxiosResponse,
  type AxiosError,
  type InternalAxiosRequestConfig,
  type AxiosRequestConfig,
} from 'axios'

function getHostNameFromUrl(url: string) {
  if (!url) {
    return null
  }

  const matches = url.match(/^(https|http)?:\/\/([^/?#]+)(?:[/?#]|$)/i)

  return matches && matches[2]
}

/**
 * Client-side helper only, not meant for server-side
 * Deciding when its an internal DHL api call or external 3rd party like mopinion, etc
 */
function isInternalUrl(url: string): boolean {
  const hostName = getHostNameFromUrl(url)
  // if the hostName is null then it is just a path and that means it is on a DHL host
  return hostName ? hostName === window?.location?.hostname : true
}

export type Method = AxiosMethod

export type ResponseInterceptor = {
  onFulfilled?: (value: AxiosResponse) => AxiosResponse | Promise<AxiosResponse>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onRejected?: (error: any) => any
}

/** @deprecated Use useResponseInterceptor from @dhl/axios instead */
export const addResponseInterceptor = (responseInterceptor: ResponseInterceptor): number =>
  axios.interceptors.response.use(responseInterceptor.onFulfilled, responseInterceptor.onRejected)

/** @deprecated Use useResponseInterceptor from @dhl/axios instead */
export const clearResponseInterceptor = (id: number): void => {
  axios.interceptors.response.eject(id)
}

export type RequestInterceptor = {
  onRequest?: (value: InternalAxiosRequestConfig) => InternalAxiosRequestConfig
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onRejected?: (error: any) => any
}

/** @deprecated Use useRequestInterceptor from @dhl/axios instead */
export const addRequestInterceptor = (requestInterceptor: RequestInterceptor): number =>
  axios.interceptors.request.use(requestInterceptor.onRequest, requestInterceptor.onRejected)

/** @deprecated Use useRequestInterceptor from @dhl/axios instead */
export const clearRequestInterceptor = (id: number): void => axios.interceptors.request.eject(id)

export type FetchHeaders = {
  Authorization?: string
}

// eslint-disable-next-line @typescript-eslint/naming-convention
export function enrichHeadersWithCSRFToken<H>(url: string, headers: H | {}): {} {
  // The X-CSRF-TOKEN is sent only for the requests going to the DHL APIs.
  return isInternalUrl(url)
    ? {
        'X-CSRF-TOKEN': true,
        ...headers,
      }
    : { ...headers }
}

export type FetchOptions<I = undefined, H = undefined> = {
  url: string
  /**
   * The HTTP method
   * @default 'GET'
   */
  method?: Method
  body?: I
  headers?: H | {}
  timeout?: number
}

/** @deprecated Use @dhl/axios instead */
export function fetch<I, O, H extends FetchHeaders = {}>({
  url,
  method = 'GET',
  body,
  headers = {},
  timeout,
}: FetchOptions<I, H>): Promise<O> {
  const config: AxiosRequestConfig = {
    url,
    method,
    data: body,
    headers: enrichHeadersWithCSRFToken<H>(url, headers),
    timeout,
  }

  return axios
    .request<O>(config)
    .then((response: AxiosResponse<O>) => response.data)
    .catch((error: AxiosError) => Promise.reject(error.response))
}

export type FetchNoCatchOptions<I, H> = FetchOptions<I, H> & {
  // legacy fetch method doesn't provide a way to abort, in our version we want this feature so not typed globally
  signal?: AbortSignal
  timeout?: number
}

/**
 * Fetch mechanism which leaves error mechanism to the consumer itself.
 * For example React Query will catch the error, and make it available as error field
 * This version also support giving queries a max timeout
 *
 * @deprecated Try to use @dhl/axios instead, but
 * when having multiple Datasources this setup is still valid with React Query!
 */
export function fetchNoCatch<I, O, H extends FetchHeaders = {}>({
  url,
  method = 'GET',
  body,
  headers = {},
  signal,
  timeout,
}: FetchNoCatchOptions<I, H>): Promise<O> {
  const config: AxiosRequestConfig = {
    url,
    method,
    data: body,
    headers: enrichHeadersWithCSRFToken<H>(url, headers),
    signal,
    timeout,
  }

  return axios.request<O>(config).then((response: AxiosResponse<O>) => response.data)
}
